/**
 * Checklist items in Loro (spec §7.4.11): a `LoroTree` inside the block body, one node per item with
 * `text` (LoroText), `checked`, `checkedAt`, `checkedBy`, `createdAt`, `createdBy`. Every operation
 * commits once, so it is one undo step and one sync update.
 */
import { LoroMap, LoroText, LoroTree, type LoroDoc, type LoroTreeNode, type TreeID } from "loro-crdt";

export interface ItemRow {
  id: TreeID;
  depth: number;
  parent: TreeID | null;
  /** index among siblings */
  index: number;
  checked: boolean;
  /** has children (for "hide completed" and delete rules) */
  hasChildren: boolean;
  /** hidden because this item or an ancestor is checked and completed items are hidden */
  hiddenByCompleted: boolean;
}

export interface ChecklistStats {
  done: number;
  total: number;
}

export function itemsTree(body: LoroMap): LoroTree {
  const t = body.get("items");
  if (t instanceof LoroTree) return t;
  const tree = body.setContainer("items", new LoroTree());
  tree.enableFractionalIndex(0);
  return tree;
}

export function initChecklistBody(body: LoroMap): void {
  body.setContainer("title", new LoroText());
  itemsTree(body);
}

function children(tree: LoroTree, parent: TreeID | null): LoroTreeNode[] {
  if (parent === null) return tree.roots();
  return tree.getNodeByID(parent)?.children() ?? [];
}

/** Items in display order (depth-first), with depth and sibling index. */
export function readItems(body: LoroMap): ItemRow[] {
  const t = body.get("items");
  if (!(t instanceof LoroTree)) return [];
  const out: ItemRow[] = [];
  const walk = (nodes: LoroTreeNode[], depth: number, parent: TreeID | null, hiddenAbove: boolean) => {
    nodes.forEach((n, index) => {
      const checked = n.data.get("checked") === true;
      const kids = n.children() ?? [];
      out.push({ id: n.id, depth, parent, index, checked, hasChildren: kids.length > 0, hiddenByCompleted: hiddenAbove || checked });
      walk(kids, depth + 1, n.id, hiddenAbove || checked);
    });
  };
  walk(t.roots(), 0, null, false);
  return out;
}

export function stats(rows: ItemRow[]): ChecklistStats {
  return { done: rows.filter((r) => r.checked).length, total: rows.length };
}

export function itemText(body: LoroMap, id: TreeID): LoroText | null {
  const n = itemsTree(body).getNodeByID(id);
  if (!n) return null;
  const t = n.data.get("text");
  return t instanceof LoroText ? t : n.data.setContainer("text", new LoroText());
}

/** New item as the next sibling of `after` (or the last root when `after` is null). */
export function addItem(doc: LoroDoc, body: LoroMap, after: TreeID | null, userId: string, text = ""): TreeID {
  const tree = itemsTree(body);
  let node: LoroTreeNode;
  if (after === null) {
    node = tree.createNode(undefined, tree.roots().length);
  } else {
    const ref = tree.getNodeByID(after);
    if (!ref) throw new Error("item not found");
    const parent = ref.parent();
    node = tree.createNode(parent?.id, (ref.index() ?? 0) + 1);
  }
  node.data.setContainer("text", new LoroText()).insert(0, text);
  node.data.set("checked", false);
  node.data.set("createdAt", Date.now());
  node.data.set("createdBy", userId);
  doc.commit();
  return node.id;
}

/** Enter in the middle of an item: the text after the cursor becomes a new item after it. */
export function splitItem(doc: LoroDoc, body: LoroMap, id: TreeID, at: number, userId: string): TreeID {
  const text = itemText(body, id);
  if (!text) throw new Error("item not found");
  const tail = text.toString().slice(at);
  if (tail.length) text.delete(at, tail.length);
  return addItem(doc, body, id, userId, tail);
}

/** Deletes an item and its sub-items. */
export function deleteItem(doc: LoroDoc, body: LoroMap, id: TreeID): void {
  itemsTree(body).delete(id);
  doc.commit();
}

/** Indent under the previous sibling (as its last child); no-op for a first sibling. */
export function indentItem(doc: LoroDoc, body: LoroMap, id: TreeID): boolean {
  const tree = itemsTree(body);
  const node = tree.getNodeByID(id);
  if (!node) return false;
  const siblings = children(tree, node.parent()?.id ?? null);
  const i = siblings.findIndex((s) => s.id === id);
  if (i <= 0) return false;
  const prev = siblings[i - 1]!;
  tree.move(id, prev.id, (prev.children() ?? []).length);
  doc.commit();
  return true;
}

/** Outdent to the parent's level, right after the parent; no-op for a root item. */
export function outdentItem(doc: LoroDoc, body: LoroMap, id: TreeID): boolean {
  const tree = itemsTree(body);
  const node = tree.getNodeByID(id);
  const parent = node?.parent();
  if (!node || !parent) return false;
  tree.move(id, parent.parent()?.id, (parent.index() ?? 0) + 1);
  doc.commit();
  return true;
}

/** Move up (-1) or down (+1) among siblings. */
export function moveItem(doc: LoroDoc, body: LoroMap, id: TreeID, delta: -1 | 1): boolean {
  const tree = itemsTree(body);
  const node = tree.getNodeByID(id);
  if (!node) return false;
  const siblings = children(tree, node.parent()?.id ?? null);
  const i = siblings.findIndex((s) => s.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= siblings.length) return false;
  if (delta < 0) node.moveBefore(siblings[j]!);
  else node.moveAfter(siblings[j]!);
  doc.commit();
  return true;
}

/** Drag and drop: place `id` as the next (`after`) or previous sibling of `target`, taking its
 *  sub-items along. Refused onto itself or into its own subtree. */
export function placeNear(doc: LoroDoc, body: LoroMap, id: TreeID, target: TreeID, where: "before" | "after"): boolean {
  if (id === target) return false;
  const tree = itemsTree(body);
  const node = tree.getNodeByID(id);
  const ref = tree.getNodeByID(target);
  if (!node || !ref) return false;
  for (let p = ref.parent(); p; p = p.parent()) if (p.id === id) return false;
  if (where === "after") node.moveAfter(ref);
  else node.moveBefore(ref);
  doc.commit();
  return true;
}

export const placeAfter = (doc: LoroDoc, body: LoroMap, id: TreeID, target: TreeID) => placeNear(doc, body, id, target, "after");

export function toggleItem(doc: LoroDoc, body: LoroMap, id: TreeID, checked: boolean, userId: string): void {
  const node = itemsTree(body).getNodeByID(id);
  if (!node) return;
  node.data.set("checked", checked);
  node.data.set("checkedAt", checked ? Date.now() : null);
  node.data.set("checkedBy", checked ? userId : null);
  doc.commit();
}
